import * as React from 'react';
import { NString, processUnionEntries } from '@mobalytics/shared';
import { ControllerFieldState, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import { useHistory } from 'react-router';
import { genPaymentPlanSelectionPath } from '../../../../utils/formatter/path.formatter';
import { treasuryApolloClient } from '../../../../treasury-apollo-client';
import { usePlansWithCouponQueryLazyQuery } from '../../_gql/__generated/plans-with-coupon.query.gql.generated';
import { PlanFragment } from '../../_gql/fragments/__generated/plan.fragment.gql.generated';
import { getQuerySearchWithCoupon } from '../../utils/get-coupon-url';

export type CouponFormFields = {
  coupon: string;
};

type Params = {
  coupon: NString;
  loading: boolean;
  isCouponApplied: boolean;
  productAbbreviation: NString;
};

type Result = {
  loading: boolean;
  isFormVisible: boolean;
  setIsFormVisible: React.Dispatch<React.SetStateAction<boolean>>;
  couponField: ControllerRenderProps<CouponFormFields, 'coupon'>;
  couponFieldState: ControllerFieldState;
  applyCoupon: () => void;
  removeCoupon: () => void;
  closeForm: () => void;
};

export const useCoupon = (params: Params): Result => {
  const { coupon, isCouponApplied, loading, productAbbreviation } = params;
  const { push } = useHistory();
  const [isFormVisible, setIsFormVisible] = React.useState<boolean>(false);

  const { t } = useTranslation();

  const [validateCoupon, { loading: isCouponValidating }] = usePlansWithCouponQueryLazyQuery({
    client: treasuryApolloClient,
    fetchPolicy: 'no-cache',
  });

  const { control, handleSubmit, clearErrors, setValue, setError, reset } = useForm<CouponFormFields>({
    mode: 'onSubmit',
    defaultValues: {
      coupon: coupon || '',
    },
  });

  const { field, fieldState } = useController({
    name: 'coupon',
    control,
    rules: {
      required: { message: t('Enter coupon'), value: true },
    },
  });

  React.useEffect(() => {
    if (coupon && isCouponApplied) {
      setIsFormVisible(false);
    }
  }, [coupon, isCouponApplied]);

  React.useEffect(() => {
    if (coupon && !isCouponApplied && !loading) {
      setError('coupon', { message: t('Invalid coupon') });
    }
  }, [coupon, isCouponApplied, loading, setError, t]);

  // The coupon goes into the url (and from there into every plans query),
  // so it is validated against the backend first: an invalid coupon must not
  // end up in the url — once there, it just keeps producing plans without discount
  const applyCoupon = handleSubmit(async (data: CouponFormFields) => {
    clearErrors('coupon');
    if (!productAbbreviation) {
      return;
    }
    const { data: validationData } = await validateCoupon({
      variables: { plansInput: { couponName: data.coupon, productAbbreviation } },
    });
    const plans = processUnionEntries<PlanFragment>(
      validationData?.treasury?.payments?.plansWithCoupon?.plans,
      'TreasuryPaymentsPlan'
    );
    const isCouponValid = plans?.some((plan) => plan.appliedCoupon) ?? false;
    if (isCouponValid) {
      push(genPaymentPlanSelectionPath(getQuerySearchWithCoupon(data.coupon)));
    } else {
      setError('coupon', { message: t('Invalid coupon') });
    }
  });

  // The form state outlives the closed form, so closing it drops whatever was
  // typed (e.g. a rejected coupon) and restores the field to the coupon from the url
  const closeForm = () => {
    reset({ coupon: coupon || '' });
    setIsFormVisible(false);
  };

  const removeCoupon = async () => {
    clearErrors('coupon');
    setValue('coupon', '');
    push(genPaymentPlanSelectionPath(getQuerySearchWithCoupon(null)));
  };

  return {
    loading: loading || isCouponValidating,
    isFormVisible,
    setIsFormVisible,
    couponField: field,
    couponFieldState: fieldState,
    applyCoupon,
    removeCoupon,
    closeForm,
  };
};
